import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Button, LoadingSpinner } from '../common';
import { practiceService, PracticeSessionItem, StartSessionResponse } from '../../api/services/practice';
import { ContentDisplay } from '../Content';
import { useAuth } from '../../contexts/AuthContext';
import { APIResponse, ContentItem, ContentType, MCQPayload, TrueFalsePayload, CompanyTag } from '@memriti/shared';
import Spinner from '../common/LoadingSpinner';
import MarkdownRenderer from '../common/MarkdownRenderer';
import { FlagIcon } from '@heroicons/react/24/outline';
import FlagQuestionModal, { FlagReason } from '../SkillDiagnostic/FlagQuestionModal';
import { assessmentService } from '../../api/services/assessment';

interface PracticeSessionProps {
  session: APIResponse<StartSessionResponse>;
  onComplete: (sessionId: number) => Promise<void>;
}

export const PracticeSession: React.FC<PracticeSessionProps> = ({ session, onComplete }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentItem, setCurrentItem] = useState<PracticeSessionItem | null>(session.data.first_item);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userAnswer, setUserAnswer] = useState<string>('');
  const [startTime, setStartTime] = useState<number | null>(Date.now());
  const [answerSubmitted, setAnswerSubmitted] = useState(false);
  const [nextItem, setNextItem] = useState<PracticeSessionItem | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<number>(session.data.session.duration_minutes * 60);
  const [isTimeUp, setIsTimeUp] = useState(false);
  const [showEndPractice, setShowEndPractice] = useState(false);
  const [similarQuestions, setSimilarQuestions] = useState<any[] | null>(null);
  const [similarError, setSimilarError] = useState<string | null>(null);
  const [isLoadingSimilar, setIsLoadingSimilar] = useState(false);
  const [aiExplanation, setAIExplanation] = useState<string | null>(null);
  const [explanationError, setExplanationError] = useState<string | null>(null);
  const [isLoadingExplanation, setIsLoadingExplanation] = useState(false);

  // State for similar questions answers
  const [similarAnswers, setSimilarAnswers] = useState<Record<number, string>>({});
  const [similarSubmitted, setSimilarSubmitted] = useState<Record<number, boolean>>({});
  const [similarQuestionsExpanded, setSimilarQuestionsExpanded] = useState(false);
  const [expandedSimilarQuestions, setExpandedSimilarQuestions] = useState<Record<number, boolean>>({});
  const [activeAITab, setActiveAITab] = useState<'similar' | 'explanation'>('similar');
  const [isFlagModalOpen, setIsFlagModalOpen] = useState(false);

  // Track time remaining
  useEffect(() => {
    if (timeRemaining <= 0) {
      setIsTimeUp(true);
      return;
    }

    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 0) {
          setIsTimeUp(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeRemaining]);

  // Convert PracticeSessionItem to ContentItem format
  const getContentItem = (item: PracticeSessionItem): ContentItem => {
    if (!item.content) {
      throw new Error('Content data not available');
    }
    return item.content;
  };

  const handleAnswer = async (answer: string) => {
    if (!currentItem || !user || !startTime) return;

    setIsSubmitting(true);
    setError(null);
    setUserAnswer(answer);

    try {
      const timeSpent = Math.floor((Date.now() - startTime) / 1000); // Convert to seconds

      const content = getContentItem(currentItem);

      // Convert answer to lowercase string for True/False questions
      const answerToSubmit = content.type === ContentType.TRUE_FALSE
        ? answer.toLowerCase()
        : answer;

      // Update answer (fast - no next item fetching)
      await practiceService.updateSessionItem(
        session.data.session.id,
        currentItem.id,
        answerToSubmit,
        timeSpent
      );

      setAnswerSubmitted(true);

      // Get next item in a separate request (can be done in background)
      try {
        const nextItemResponse = await practiceService.getNextItem(session.data.session.id);
        setNextItem(nextItemResponse.data);
      } catch (err) {
        console.error('Failed to get next item:', err);
        // Don't show error to user, they can still proceed
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit answer');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = async () => {
    if (isTimeUp) {
      setShowEndPractice(true);
      return;
    }

    // Clear similar questions and AI explanation states when moving to next question
    setSimilarQuestions(null);
    setSimilarError(null);
    setAIExplanation(null);
    setExplanationError(null);
    setIsLoadingSimilar(false);
    setIsLoadingExplanation(false);
    setSimilarAnswers({});
    setSimilarSubmitted({});
    setSimilarQuestionsExpanded(false);
    setExpandedSimilarQuestions({});
    setActiveAITab('similar');

    if (nextItem) {
      setCurrentItem(nextItem);
      setNextItem(null);
      setAnswerSubmitted(false);
      setUserAnswer('');
      setStartTime(Date.now());
    } else {
      // Get the next item with company tag filtering if available
      try {
        const nextItemResponse = await practiceService.getNextItem(session.data.session.id);
        if (nextItemResponse.data) {
          setCurrentItem(nextItemResponse.data);
          setAnswerSubmitted(false);
          setUserAnswer('');
          setStartTime(Date.now());
        } else {
          // No more items, complete the session
          await onComplete(session.data.session.id);
          navigate('/dashboard');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to get next item');
      }
    }
  };

  const handleEndPractice = async () => {
    await onComplete(session.data.session.id);
    navigate('/dashboard');
  };

  const handleFlagQuestion = async (reason: FlagReason, description?: string) => {
    if (!currentItem || !currentItem.content) return;

    try {
      await assessmentService.flagQuestion(
        currentItem.content.id,
        currentItem.content.type,
        reason,
        description
      );
      console.log('Question flagged successfully');
    } catch (error) {
      console.error('Failed to flag question:', error);
      throw error; // Re-throw to let modal handle it
    }
  };

  // Handler for similar questions
  const handleShowSimilarQuestions = async () => {
    if (!currentItem) return;

    const currentContent = getContentItem(currentItem);
    setIsLoadingSimilar(true);
    setSimilarError(null);
    setSimilarQuestions(null);
    try {
      const res = await practiceService.getSimilarQuestions({
        topic: currentContent.microtopic.subtopic.topic.name,
        subtopic: currentContent.microtopic.subtopic.name,
        microtopic: currentContent.microtopic.name,
        content_type: currentContent.type,
        content_id: currentContent.id, // Pass the current content ID for better context
        count: 3
      });
      if (res.success && Array.isArray(res.data)) {
        // The backend now returns PracticeSessionItem objects with content attached
        // We can use them directly with the existing getContentItem function
        setSimilarQuestions(res.data);
      } else {
        setSimilarError('Could not fetch similar questions.');
      }
    } catch (err: any) {
      setSimilarError(err.message || 'Failed to fetch similar questions.');
    } finally {
      setIsLoadingSimilar(false);
    }
  };

  // Handler for AI explanation
  const handleShowAIExplanation = async () => {
    if (!currentItem) return;

    const currentContent = getContentItem(currentItem);
    setIsLoadingExplanation(true);
    setExplanationError(null);
    setAIExplanation(null);
    try {
      const res = await practiceService.getAIExplanation({
        content_id: currentContent.id, // Send content ID for context
        content_type: currentContent.type // Send content type for proper content retrieval
      });
      if (res.success && typeof res.data === 'string') {
        setAIExplanation(res.data);
      } else {
        setExplanationError('Could not fetch AI explanation.');
      }
    } catch (err: any) {
      setExplanationError(err.message || 'Failed to fetch AI explanation.');
    } finally {
      setIsLoadingExplanation(false);
    }
  };

  // Handler for similar questions answers
  const handleSimilarAnswer = (questionIndex: number, answer: string) => {
    setSimilarAnswers(prev => ({ ...prev, [questionIndex]: answer }));
    // Automatically submit the answer when an option is clicked
    setSimilarSubmitted(prev => ({ ...prev, [questionIndex]: true }));
  };

  // Helper to get similar question correct answer and explanation
  const getSimilarQuestionFeedback = (similarQuestion: any) => {
    const content = convertSimilarQuestionToContentItem(similarQuestion);
    let correctAnswer: string | undefined;
    let explanation: string | undefined;

    if (content.type === ContentType.MCQ) {
      const payload = content.payload as MCQPayload;
      correctAnswer = payload.correct_answer;
      explanation = payload.explanation;
    } else if (content.type === ContentType.TRUE_FALSE) {
      const payload = content.payload as TrueFalsePayload;
      correctAnswer = payload.answer.toString();
      explanation = payload.explanation;
    }

    return { correctAnswer, explanation };
  };

  // Helper to convert similar question dictionary to ContentItem
  function convertSimilarQuestionToContentItem(similarQuestion: any): ContentItem {
    // The similar question is now a dictionary with content attached
    const content = similarQuestion.content;
    return {
      id: content.id,
      type: content.type,
      difficulty: content.difficulty,
      payload: content.payload,
      microtopic: content.microtopic,
      microtopic_id: content.microtopic_id,
      created_at: content.created_at,
      updated_at: content.updated_at,
      company_tags: content.company_tags || []
    };
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  if (!currentItem) {
    return (
      <Card className="p-6 max-w-2xl mx-auto dark:bg-gray-800">
        <div className="text-center py-8">
          <h3 className="text-xl font-semibold mb-4 dark:text-white">No More Questions</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            You've reviewed all available questions. Great job! Come back later for more practice.
          </p>
          <Button
            onClick={handleEndPractice}
            variant="primary"
            className="w-full"
          >
            End Practice
          </Button>
        </div>
      </Card>
    );
  }

  // Reason message
  let reasonMessage = '';
  let reasonIcon = '';
  let reasonExplanation = '';
  switch (currentItem.reason) {
    case 'new_content':
      reasonMessage = 'This is a new question for you!';
      reasonIcon = '🆕';
      reasonExplanation = 'New content helps you learn fresh material.';
      break;
    case 'weak_area':
      reasonMessage = 'You previously answered this incorrectly.';
      reasonIcon = '🔁';
      reasonExplanation = 'Revisiting questions you got wrong helps strengthen your understanding.';
      break;
    case 'due_for_review':
      reasonMessage = 'It\'s been a while since you last saw this question.';
      reasonIcon = '⏰';
      reasonExplanation = 'Regular reviews help reinforce memory.';
      break;
    case 'reinforcement':
      reasonMessage = 'Scheduled review to reinforce your memory.';
      reasonIcon = '🔄';
      reasonExplanation = 'Reinforcement learning solidifies knowledge.';
      break;
    case 'exploration':
      reasonMessage = 'Random question to keep things fresh!';
      reasonIcon = '🎲';
      reasonExplanation = 'Exploration introduces variety and challenge.';
      break;
    default:
      reasonMessage = '';
      reasonIcon = '';
      reasonExplanation = '';
  }

  const content = getContentItem(currentItem);
  const topicName = content.microtopic.subtopic.topic.name;
  const subtopicName = content.microtopic.subtopic.name;

  let correctAnswer: string | undefined;
  let explanation: string | undefined;

  if (content.type === ContentType.MCQ) {
    const payload = content.payload as MCQPayload;
    correctAnswer = payload.correct_answer;
    explanation = payload.explanation;
  } else if (content.type === ContentType.TRUE_FALSE) {
    const payload = content.payload as TrueFalsePayload;
    correctAnswer = payload.answer.toString();
    explanation = payload.explanation;
  }

  // Calculate isCorrect based on user's answer matching correct answer
  const isAnswerCorrect = answerSubmitted && userAnswer === correctAnswer;

  return (
    <Card className="p-6 max-w-2xl mx-auto dark:bg-gray-800">
      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-md">
          {error}
        </div>
      )}

      <div>
        {!showEndPractice && (
          <div className="mb-4">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-700 dark:text-gray-300">
            {topicName} <span className="text-gray-500 dark:text-gray-400">&gt;</span> {subtopicName}
          </h2>
              <div className="flex items-center gap-3">
                {content.company_tags && content.company_tags.length > 0 && (
                  <div className="flex gap-2">
                    {content.company_tags.map((tag: CompanyTag) => (
                      <span
                        key={tag.id}
                        className="px-3 py-1 rounded-full text-sm font-medium text-white"
                        style={{ backgroundColor: tag.color || '#1890ff' }}
                      >
                        {tag.name}
                      </span>
                    ))}
                  </div>
                )}
                {/* Flag Button */}
                <button
                  onClick={() => setIsFlagModalOpen(true)}
                  className="flex-shrink-0 p-2 rounded-lg transition-colors text-yellow-500 dark:text-yellow-400 hover:text-yellow-600 dark:hover:text-yellow-300 hover:bg-yellow-50 dark:hover:bg-yellow-900/20"
                  title="Report this question"
                >
                  <FlagIcon className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        )}
        {reasonMessage && !showEndPractice && (
          <div className="mb-2 p-4 rounded-lg bg-gradient-to-r from-blue-100 to-indigo-100 dark:from-blue-900/30 dark:to-indigo-900/30 text-indigo-800 dark:text-indigo-200 text-sm font-medium border border-indigo-200 dark:border-indigo-800 shadow-lg animate-fade-in">
            <div className="flex items-center space-x-3">
              <span className="text-lg">{reasonIcon}</span>
              <div>
                <span className="block font-bold">{reasonMessage}</span>
                <span className="text-gray-600 dark:text-gray-400 italic text-xs">{reasonExplanation}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {showEndPractice ? (
        <div className="text-center py-8">
          <h3 className="text-xl font-semibold mb-4 dark:text-white">Time's up!</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Research shows that shorter, more frequent sessions are most effective for learning.
          </p>
          <Button
            onClick={handleEndPractice}
            variant="primary"
            className="w-full"
          >
            End Practice
          </Button>
        </div>
      ) : (
        <>
          <ContentDisplay
            content={content}
            onAnswer={handleAnswer}
            userAnswer={userAnswer}
            answerSubmitted={answerSubmitted}
            isCorrect={isAnswerCorrect}
            correctAnswer={correctAnswer}
            explanation={explanation}
          />

          {/* New AI options after answer is submitted */}
          {answerSubmitted && (
            <div className="mt-6">
              {/* AI Features Tabs */}
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm">
                {/* Tab Headers */}
                <div className="flex border-b border-slate-200 dark:border-slate-700">
                  <button
                    onClick={() => setActiveAITab('similar')}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-all duration-200 ${
                      activeAITab === 'similar'
                        ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20'
                        : 'text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/50'
                    }`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                    🧠 Similar Questions
                    {similarQuestions && (
                      <span className="ml-1 px-2 py-0.5 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full">
                        {similarQuestions.length}
                      </span>
                    )}
                  </button>
                  <button
                    onClick={() => setActiveAITab('explanation')}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-all duration-200 ${
                      activeAITab === 'explanation'
                        ? 'text-emerald-600 dark:text-emerald-400 border-b-2 border-emerald-600 dark:border-emerald-400 bg-emerald-50 dark:bg-emerald-900/20'
                        : 'text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/50'
                    }`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    🤖 AI Explanation
                  </button>
                </div>

                {/* Tab Content */}
                <div className="p-4">
                  {/* Similar Questions Tab */}
                  {activeAITab === 'similar' && (
                    <div className="space-y-4">
                      {/* Loading spinner for similar questions */}
                      {isLoadingSimilar && (
                        <div className="flex items-center justify-center py-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400 dark:border-blue-300"></div>
                          <span className="ml-3 text-slate-600 dark:text-slate-300 font-medium">🧠 AI is generating similar questions...</span>
                        </div>
                      )}

                      {/* Error for similar questions */}
                      {similarError && (
                        <div className="text-red-600 dark:text-red-400 text-center py-4">
                          {similarError}
                        </div>
                      )}

                      {/* Similar Questions Content */}
                      {similarQuestions && !isLoadingSimilar && (
                        <div className="space-y-3">
                          {similarQuestions.map((sessionItem, idx) => {
                            const similarContent = convertSimilarQuestionToContentItem(sessionItem);
                            const { correctAnswer, explanation } = getSimilarQuestionFeedback(sessionItem);
                            const userAnswer = similarAnswers[idx] || '';
                            const isSubmitted = similarSubmitted[idx] || false;
                            const isCorrect = isSubmitted && userAnswer === correctAnswer;
                            const isExpanded = expandedSimilarQuestions[idx] || false;

                            // Get question preview text
                            let questionPreview = '';
                            if (similarContent.type === ContentType.MCQ) {
                              const payload = similarContent.payload as MCQPayload;
                              questionPreview = payload.question.length > 80
                                ? payload.question.substring(0, 80) + '...'
                                : payload.question;
                            } else if (similarContent.type === ContentType.TRUE_FALSE) {
                              const payload = similarContent.payload as TrueFalsePayload;
                              questionPreview = payload.statement.length > 80
                                ? payload.statement.substring(0, 80) + '...'
                                : payload.statement;
                            }

                            return (
                              <div key={idx} className="group relative">
                                {/* Question Preview Card */}
                                <div
                                  className={`cursor-pointer transition-all duration-200 rounded-xl border-2 p-4 ${
                                    isExpanded
                                      ? 'border-blue-300 bg-blue-50 dark:border-blue-600 dark:bg-blue-900/20'
                                      : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 bg-white dark:bg-gray-800 hover:shadow-md'
                                  }`}
                                  onClick={() => setExpandedSimilarQuestions(prev => ({ ...prev, [idx]: !isExpanded }))}
                                >
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2 mb-2">
                                        <span className="text-xs font-semibold text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/30 px-2 py-1 rounded-full">
                                          Q{idx + 1}
                                        </span>
                                        {isSubmitted && (
                                          <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full ${
                                            isCorrect
                                              ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                                              : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                                          }`}>
                                            {isCorrect ? (
                                              <>
                                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                </svg>
                                                Correct
                                              </>
                                            ) : (
                                              <>
                                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                                </svg>
                                                Incorrect
                                              </>
                                            )}
                                          </span>
                                        )}
                                      </div>
                                      <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
                                        {questionPreview}
                                      </p>
                                    </div>
                                    <div className="ml-3 flex-shrink-0">
                                      <div className={`transition-transform duration-200 ${
                                        isExpanded ? 'rotate-180' : 'rotate-0'
                                      }`}>
                                        <svg className="w-5 h-5 text-slate-400 group-hover:text-slate-600 dark:group-hover:text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                {/* Expanded Question Content */}
                                {isExpanded && (
                                  <div className="mt-3 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700">
                                    <ContentDisplay
                                      content={similarContent}
                                      onAnswer={(answer) => handleSimilarAnswer(idx, answer)}
                                      userAnswer={userAnswer}
                                      answerSubmitted={isSubmitted}
                                      isCorrect={isCorrect}
                                      correctAnswer={correctAnswer}
                                      explanation={explanation}
                                    />
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Empty state for similar questions */}
                      {!similarQuestions && !isLoadingSimilar && !similarError && (
                        <div className="text-center py-8">
                          <svg className="w-16 h-16 mx-auto mb-4 text-slate-300 dark:text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                          </svg>
                          <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-300 mb-2">Generate Similar Questions</h3>
                          <p className="text-slate-500 dark:text-slate-400 mb-6">Get AI-powered similar questions to practice more on this topic</p>
                          <Button
                            onClick={handleShowSimilarQuestions}
                            disabled={isLoadingSimilar}
                            variant="primary"
                            className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700"
                          >
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                            </svg>
                            🧠 Generate Similar Questions
                          </Button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* AI Explanation Tab */}
                  {activeAITab === 'explanation' && (
                    <div className="space-y-4">
                      {/* Loading spinner for AI explanation */}
                      {isLoadingExplanation && (
                        <div className="flex items-center justify-center py-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-400 dark:border-emerald-300"></div>
                          <span className="ml-3 text-slate-600 dark:text-slate-300 font-medium">🤖 AI is analyzing and explaining...</span>
                        </div>
                      )}

                      {/* Error for AI explanation */}
                      {explanationError && (
                        <div className="text-red-600 dark:text-red-400 text-center py-4">
                          {explanationError}
                        </div>
                      )}

                      {/* AI Explanation Content */}
                      {aiExplanation && !isLoadingExplanation && (
                        <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-4 border border-emerald-200 dark:border-emerald-800">
                          <div className="flex items-center gap-2 mb-3">
                            <svg className="w-5 h-5 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className="font-semibold text-emerald-800 dark:text-emerald-200">AI Deep Explanation</span>
                          </div>
                          <div className="text-slate-700 dark:text-slate-300">
                            <MarkdownRenderer content={aiExplanation} />
                          </div>
                        </div>
                      )}

                      {/* Empty state for AI explanation */}
                      {!aiExplanation && !isLoadingExplanation && !explanationError && (
                        <div className="text-center py-8">
                          <svg className="w-16 h-16 mx-auto mb-4 text-slate-300 dark:text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-300 mb-2">Get AI Explanation</h3>
                          <p className="text-slate-500 dark:text-slate-400 mb-6">Get a detailed AI explanation of why your answer was correct or incorrect</p>
                          <Button
                            onClick={handleShowAIExplanation}
                            disabled={isLoadingExplanation}
                            variant="primary"
                            className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700"
                          >
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            🤖 Get AI Explanation
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="mt-4 flex justify-end">
            <Button
              onClick={answerSubmitted ? handleNext : () => handleAnswer(userAnswer)}
              variant="primary"
              disabled={!userAnswer}
            >
              Next
            </Button>
          </div>
        </>
      )}

      {/* Flag Question Modal */}
      {currentItem && currentItem.content && (
        <FlagQuestionModal
          isOpen={isFlagModalOpen}
          onClose={() => setIsFlagModalOpen(false)}
          onFlag={handleFlagQuestion}
          questionText={
            content.type === ContentType.MCQ
              ? (content.payload as MCQPayload).question
              : content.type === ContentType.TRUE_FALSE
              ? (content.payload as TrueFalsePayload).statement
              : undefined
          }
        />
      )}
    </Card>
  );
};